import { useCallback, useEffect, useState } from "react";
import { message } from "antd";
import axAPI from "api/axios-instance";
import {
  MedicinePublication,
  MedicinePublicationFormValues,
  MedicinePublicationKind,
  MedicinePublicationsResponse,
} from "./types";

const PUBLICATIONS_URL = "/cms/medicine/publications";

/** Бэк отдаёт текст ошибки строкой в теле — показываем его, а не «Request failed». */
const errorText = (error: any, fallback: string) =>
  typeof error?.response?.data === "string"
    ? error.response.data
    : error?.message || fallback;

export const useMedicinePublications = (kind: MedicinePublicationKind) => {
  const [publications, setPublications] = useState<MedicinePublication[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [removingIds, setRemovingIds] = useState<number[]>([]);

  const loadPublications = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axAPI.get<MedicinePublicationsResponse>(
        PUBLICATIONS_URL,
        { params: { kind } }
      );
      setPublications(data.publications || []);
    } catch (error: any) {
      message.error(errorText(error, "Не удалось загрузить публикации"));
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    loadPublications();
  }, [loadPublications]);

  const savePublication = async (values: MedicinePublicationFormValues) => {
    setSaving(true);
    try {
      await axAPI.post(`${PUBLICATIONS_URL}/upsert`, { ...values, kind });
      message.success("Публикация сохранена");
      await loadPublications();
    } catch (error: any) {
      message.error(errorText(error, "Не удалось сохранить публикацию"));
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const removePublication = async (id: number) => {
    setRemovingIds((state) => [...state, id]);
    try {
      await axAPI.post(`${PUBLICATIONS_URL}/remove/${id}`);
      message.success("Публикация удалена");
      await loadPublications();
    } catch (error: any) {
      message.error(errorText(error, "Не удалось удалить публикацию"));
    } finally {
      setRemovingIds((state) => state.filter((item) => item !== id));
    }
  };

  /**
   * Сдвиг на одну позицию. Список переставляем сразу, чтобы кнопка не «залипала»
   * на время запроса, а порядок с сервера подтягиваем следом.
   */
  const movePublication = async (id: number, direction: -1 | 1) => {
    const index = publications.findIndex((item) => item.id === id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= publications.length) {
      return;
    }

    const reordered = [...publications];
    [reordered[index], reordered[target]] = [
      reordered[target],
      reordered[index],
    ];
    setPublications(reordered);

    setReordering(true);
    try {
      await axAPI.post(`${PUBLICATIONS_URL}/reorder`, {
        kind,
        ids: reordered.map((item) => item.id),
      });
      await loadPublications();
    } catch (error: any) {
      message.error(errorText(error, "Не удалось изменить порядок"));
      await loadPublications();
    } finally {
      setReordering(false);
    }
  };

  return {
    loading,
    publications,
    removingIds,
    reordering,
    saving,
    movePublication,
    removePublication,
    savePublication,
  };
};
